import React from 'react';
import {
  MSP_DIAG_ROW_BLANK,
  MSP_DIAG_MIN_FILAS,
  ensureTablaDiagnosticosMSPStructure,
  splitDiagnosticosDual,
  diagnosticosConDatos,
} from '../../data/planDiagnosticoMsp';
import MspReadEmpty from './MspReadEmpty';
import MspSelectionBox from './MspSelectionBox';

function PreDefCell({ tipo, value, onToggle, readOnly, label }) {
  const active = value === tipo;
  return (
    <td
      className={`msp-diag-cell msp-diag-cell--mark msp-diag-cell--mark-${tipo === 'definitivo' ? 'def' : 'pre'}${active ? ' msp-diag-cell--mark-on' : ''}`}
    >
      <MspSelectionBox
        variant="cell"
        selected={active}
        readOnly={readOnly}
        onToggle={onToggle}
        ariaLabel={label}
      />
    </td>
  );
}

function DiagHalfTable({ items, readOnly, onUpdateRow, onRemoveRow, canRemove, blockLabel }) {
  return (
    <div className="msp-diag-half">
      <div className="msp-diag-half__label">{blockLabel}</div>
      <div className="msp-diag-half__scroll">
        <table className="msp-diag-table">
          <thead>
            <tr>
              <th className="msp-diag-th msp-diag-th--num" scope="col">
                #
              </th>
              <th className="msp-diag-th msp-diag-th--dx" scope="col">
                Diagnóstico
              </th>
              <th className="msp-diag-th msp-diag-th--cie" scope="col">
                CIE-10
              </th>
              <th className="msp-diag-th msp-diag-th--pre" scope="col" title="Presuntivo">
                <span className="msp-diag-th__badge msp-diag-th__badge--pre">PRE</span>
              </th>
              <th className="msp-diag-th msp-diag-th--def" scope="col" title="Definitivo">
                <span className="msp-diag-th__badge msp-diag-th__badge--def">DEF</span>
              </th>
              {!readOnly && canRemove && (
                <th className="msp-diag-th msp-diag-th--act" scope="col" aria-label="Quitar" />
              )}
            </tr>
          </thead>
          <tbody>
            {items.map(({ row, index }) => {
              const hasData = Boolean(row.diagnostico?.trim() || row.cie10?.trim());
              return (
                <tr
                  key={`diag-${index}`}
                  className={`msp-diag-row${hasData ? ' msp-diag-row--filled' : ''}`}
                >
                  <td className="msp-diag-cell msp-diag-cell--num">{index + 1}</td>
                  <td className="msp-diag-cell msp-diag-cell--dx">
                    {readOnly ? (
                      <span className="msp-diag-text">{row.diagnostico || '—'}</span>
                    ) : (
                      <input
                        type="text"
                        className="msp-diag-input"
                        value={row.diagnostico}
                        onChange={(e) => onUpdateRow(index, { diagnostico: e.target.value })}
                        placeholder="Ej. Caries dental"
                        aria-label={`Diagnóstico ${index + 1}`}
                      />
                    )}
                  </td>
                  <td className="msp-diag-cell msp-diag-cell--cie">
                    {readOnly ? (
                      <span className="msp-diag-text msp-diag-text--cie">{row.cie10 || '—'}</span>
                    ) : (
                      <input
                        type="text"
                        className="msp-diag-input msp-diag-input--cie"
                        value={row.cie10}
                        onChange={(e) => onUpdateRow(index, { cie10: e.target.value })}
                        placeholder="K02.1"
                        aria-label={`CIE-10 fila ${index + 1}`}
                      />
                    )}
                  </td>
                  <PreDefCell
                    tipo="presuntivo"
                    value={row.tipo}
                    readOnly={readOnly}
                    label={`Presuntivo — fila ${index + 1}`}
                    onToggle={() => onUpdateRow(index, { tipo: 'presuntivo' })}
                  />
                  <PreDefCell
                    tipo="definitivo"
                    value={row.tipo}
                    readOnly={readOnly}
                    label={`Definitivo — fila ${index + 1}`}
                    onToggle={() =>
                      onUpdateRow(index, {
                        tipo: row.tipo === 'definitivo' ? 'presuntivo' : 'definitivo',
                      })
                    }
                  />
                  {!readOnly && canRemove && (
                    <td className="msp-diag-cell msp-diag-cell--act">
                      <button
                        type="button"
                        className="msp-diag-remove"
                        title="Quitar fila"
                        onClick={() => onRemoveRow(index)}
                        aria-label={`Quitar diagnóstico ${index + 1}`}
                      >
                        <i className="bi bi-trash" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Plan de diagnóstico MSP — dos bloques (# · diagnóstico · CIE · PRE · DEF).
 */
export default function PlanDiagnosticoMspTable({
  rows = [],
  onChange,
  readOnly = false,
  showSectionTitle = true,
}) {
  const filled = diagnosticosConDatos(rows);

  if (readOnly) {
    if (filled.length === 0) {
      return (
        <MspReadEmpty
          icon="bi-clipboard2-pulse"
          message="No hay diagnósticos registrados en el plan MSP."
        />
      );
    }
    return (
      <div className="msp-read-diag-list">
        {showSectionTitle && (
          <div className="msp-read-inline-hdr">
            <span>PRE = Presuntivo · DEF = Definitivo</span>
          </div>
        )}
        {filled.map((row, index) => (
          <article key={`diag-read-${index}`} className="msp-read-diag-card">
            <span className="msp-read-diag-card__num">{index + 1}</span>
            <div className="msp-read-diag-card__body">
              <h5 className="msp-read-diag-card__title">{row.diagnostico}</h5>
              <div className="msp-read-diag-card__meta">
                {row.cie10 ? (
                  <span className="msp-read-tag msp-read-tag--cie">
                    <i className="bi bi-hash" />
                    CIE {row.cie10}
                  </span>
                ) : null}
                <span
                  className={`msp-read-tag msp-read-tag--tipo msp-read-tag--${row.tipo === 'definitivo' ? 'def' : 'pre'}`}
                >
                  {row.tipo === 'definitivo' ? 'DEF · Definitivo' : 'PRE · Presuntivo'}
                </span>
              </div>
            </div>
          </article>
        ))}
      </div>
    );
  }

  const sanitized = ensureTablaDiagnosticosMSPStructure(rows);
  const { left, right } = splitDiagnosticosDual(sanitized);
  const canRemove = sanitized.length > MSP_DIAG_MIN_FILAS;

  const leftLabel =
    left.length > 0
      ? `Filas ${left[0].index + 1}${left.length > 1 ? `–${left[left.length - 1].index + 1}` : ''}`
      : 'Bloque 1';
  const rightLabel =
    right.length > 0
      ? `Filas ${right[0].index + 1}${right.length > 1 ? `–${right[right.length - 1].index + 1}` : ''}`
      : 'Bloque 2';

  const updateRow = (index, patch) => {
    if (!onChange) return;
    const next = sanitized.map((r, i) => (i === index ? { ...r, ...patch } : r));
    onChange(next);
  };

  const removeRow = (index) => {
    if (!onChange || sanitized.length <= MSP_DIAG_MIN_FILAS) return;
    onChange(sanitized.filter((_, i) => i !== index));
  };

  const addPair = () => {
    if (!onChange) return;
    onChange([...sanitized, { ...MSP_DIAG_ROW_BLANK }, { ...MSP_DIAG_ROW_BLANK }]);
  };

  return (
    <div className="msp-diag-panel">
      {showSectionTitle ? (
        <div className="msp-diag-panel__hdr">
          <div className="msp-diag-panel__hdr-text">
            <i className="bi bi-search" aria-hidden />
            <div>
              <strong>Plan de diagnóstico</strong>
              <p>Registre diagnóstico, código CIE-10 y marque presuntivo o definitivo.</p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="msp-diag-guide" role="note">
        <span className="msp-diag-guide__item">
          <span className="msp-diag-guide__box msp-diag-guide__box--pre">×</span>
          <span>
            <strong>PRE</strong> = Presuntivo
          </span>
        </span>
        <span className="msp-diag-guide__item">
          <span className="msp-diag-guide__box msp-diag-guide__box--def">×</span>
          <span>
            <strong>DEF</strong> = Definitivo
          </span>
        </span>
        <span className="msp-diag-guide__hint">Una × por fila · pulse el cuadro correspondiente</span>
      </div>

      <div className="msp-diag-dual">
        <DiagHalfTable
          items={left}
          readOnly={readOnly}
          blockLabel={leftLabel}
          onUpdateRow={updateRow}
          onRemoveRow={removeRow}
          canRemove={canRemove}
        />
        <DiagHalfTable
          items={right}
          readOnly={readOnly}
          blockLabel={rightLabel}
          onUpdateRow={updateRow}
          onRemoveRow={removeRow}
          canRemove={canRemove}
        />
      </div>

      {!readOnly && (
        <div className="msp-diag-footer">
          <button type="button" className="msp-diag-add-btn" onClick={addPair}>
            <i className="bi bi-plus-lg" aria-hidden />
            Añadir 2 diagnósticos
          </button>
          <p className="msp-diag-leyenda mb-0">
            Código <strong>CIE-10</strong> según clasificación internacional.
          </p>
        </div>
      )}
    </div>
  );
}
